import { Lock } from "lucide-react";

import { EmptyState } from "@/components/common/EmptyState";
import { PageSkeleton } from "@/components/common/PageSkeleton";

import { useDeskMe } from "./useDeskCan";

/**
 * The gate the spec puts in front of Desk: `desk:view` or nothing.
 *
 * Without it a person whose role or groups grant no Desk action still reached
 * every Desk page, each request came back 403, and the queue drew that as its
 * empty state — "nothing is being done for you" — which is a different fact
 * from "you have not been given Desk". This renders the second fact as itself,
 * and the pages behind it never fire the requests the server would refuse.
 *
 * Waits for `/desk/me` rather than guessing: a gate that flashes shut and then
 * opens teaches people the product is unreliable.
 */
export function DeskAccessGate({ children }: { children: React.ReactNode }) {
  const { data: me, isPending, isError } = useDeskMe();
  if (isPending) return <PageSkeleton />;
  // If `/desk/me` itself failed, let the page try: it has its own error state,
  // and a network blip is not a permission decision.
  if (isError || me?.actions.includes("view")) return <>{children}</>;
  return (
    <EmptyState
      icon={Lock}
      title="Desk is not switched on for you"
      description="Your role or your permission groups do not include Desk. An administrator can add you to a group that grants it."
      action={{ label: "Back to the dashboard", href: "/" }}
    />
  );
}
